/// <reference types='google.maps' />
import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { TitleService } from 'libs/portal/src/services/title/title.service';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { Endpoints } from '@huge-monorepo/portal/config';
import { ScreenSize } from 'libs/portal/src/layout/layout.service';
import { Router } from '@angular/router';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { DeviceListModalComponent } from '../../../components/map/device-list-modal/device-list-modal/device-list-modal.component';
import { Routes } from '@huge-monorepo/portal/config';
import { FilterOptions } from 'libs/portal/src/modules/tools/filters/models';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, fromEvent, Observable } from 'rxjs';

declare const google: any;
declare const MarkerClusterer: any;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit {
  @ViewChild('mapContainer', { static: false }) gmap: ElementRef;
  map: google.maps.Map;
  lat = -29.114501;
  lng = 25.411157;

  coordinates: google.maps.LatLng = new google.maps.LatLng(this.lat, this.lng);
  markerClusterer: any;
  deviceInfo: any[] = [];
  ScreenSize = ScreenSize;
  selectionEnabled = false;

  firstMapLoad = true;
  maploading = false;
  numClusters = 1;
  maxClusters = 15;
  minClusters = 1;
  activeInfoWindow: google.maps.InfoWindow;
  activeInfoMarkers: google.maps.Marker[] = [];
  currentSearch: string;

  showAllSignalStrength = false;
  showAllRingfence = false;
  signalStrengthCircles: google.maps.Circle[] = [];
  ringfenceCircles: google.maps.Circle[] = [];
  stateCircles: google.maps.Circle[] = [];
  totalOffline = 0;
  requestingClusterData = false;

  mapOptions: google.maps.MapOptions = {
    center: this.coordinates,
    zoom: 6,
    scaleControl: true,
    streetViewControl: false,
    disableDoubleClickZoom: false,
    fullscreenControl: false,
    mapTypeControl: false,
    minZoom: 4,
    zoomControl: true,
  };

  filterOptions: FilterOptions = {
    showSearch: true,
    searchPlaceholder: 'Search for devices',
    showClearChip: false,
  };

  aggregation = true;

  circles: google.maps.Circle[] = [];
  markers: google.maps.Marker[] = [];
  deviceMapData: any[] = [];
  debounceTimeout: any;
  clustering = true;
  freezeMap = false;

  mapBounds: google.maps.LatLngBoundsLiteral = {
    east: 41.7148679375,
    south: -37.47551721717827,
    west: 9.107446062500006,
    north: -20.01542582501192,
  };

  constructor(
    private titleService: TitleService,
    private genericService: GenericService,
    private endpoints: Endpoints,
    private router: Router,
    private tools: ToolsService,
    private routeDictionary: Routes,
    private zone: NgZone,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    window['MapComponent'] = this;
    window['ViewDevice'] = this.ViewDevice;
    this.titleService.pageTitle = 'Maps';
  }

  ngOnInit() {
    this.maploading = true;
    const stopScrolling = false;
    setTimeout(() => {
      this.mapInitializer();

      // Custom scroll zoom logic
      const mapContainer = document.getElementById('map'); // Ensure this ID is correct
      if (mapContainer) {
        mapContainer.addEventListener('wheel', (event: WheelEvent) => this.tools.handleZoom(event, this.map));
      }
    });

    window.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    function handleTouchMove(e) {
      if (!stopScrolling) {
        return;
      }
      e.preventDefault();
    }
  }

  clearClusterer() {
    if (this.markerClusterer) {
      this.markerClusterer.setMap(null);
    }
  }

  clearCircles() {
    if (this.circles == null) {
      return;
    }
    for (let i = 0; i < this.circles.length; i++) {
      this.circles[i].setMap(null);
      this.circles[i].setRadius(0);
    }
    this.circles = [];
  }

  clearMarkers() {
    this.clearClusterer();

    if (this.activeInfoWindow != null) {
      this.activeInfoWindow.close();
    }

    if (this.markers == null) {
      return;
    }

    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].setMap(null);
      this.markers[i].unbindAll();
    }

    this.markers = [];
  }

  toggleSelectionType() {
    this.selectionEnabled = !this.selectionEnabled;
  }

  setMapRefresh(interval: number) {
    this.totalOffline = 0;
    return setTimeout(() => {
      this.clearMarkers();
      this.clearCircles();

      const zoom = this.map.getZoom();
      if (this.map) {
        this.mapBounds = this.map.getBounds().toJSON();
      }

      if (zoom >= 10) {
        this.aggregation = false;
        setTimeout(() => {
          this.GetDeviceMapData();
        });
      } else {
        this.aggregation = true;
        if (this.numClusters > this.maxClusters) {
          this.numClusters = this.maxClusters;
        } else if (this.numClusters < this.minClusters) {
          this.numClusters = this.minClusters;
        }

        setTimeout(() => {
          this.GetClusterMapData(this.numClusters);
        });
      }
    }, interval);
  }

  mapInitializer(): void {
    this.map = new google.maps.Map(this.gmap.nativeElement, this.mapOptions);

    const filterControl = document.getElementById('chip-filter') as HTMLElement;
    this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(filterControl);

    // Create an observable for the 'bounds_changed' event
    Observable.create(observer => {
      this.map.addListener('bounds_changed', () => observer.next());
    })
    .pipe(debounceTime(500)) // Adjust the debounce time as needed
    .subscribe(() => {
      if (this.freezeMap) {
        return;
      }

      if (this.maploading) {
        return;
      }

      this.maploading = true;
      this.changeDetectorRef.detectChanges();
    });

    // Create an observable for the 'idle' event
    Observable.create(observer => {
      this.map.addListener('idle', () => observer.next());
    })
    .pipe(debounceTime(500)) // Adjust the debounce time as needed
    .subscribe(() => {
      if (this.freezeMap) {
        return;
      }

      if (this.requestingClusterData) {
        return;
      }

      this.setMapRefresh(150);
    });
  }

  changeCluster(eve) {
    if (Number.isNaN(parseInt(eve.target.value))) {
      this.numClusters = 1;
      return;
    }

    if (this.numClusters > this.maxClusters) {
      this.numClusters = this.maxClusters;
    } else if (this.numClusters < this.minClusters) {
      this.numClusters = this.minClusters;
    }

    this.numClusters = parseInt(eve.target.value);
  }

  CreateDeviceMapMarker(deviceCluster: any): boolean {
    const BASE_PATH = 'assets/map-markers/';
    let online = null;
    let lastTwelve = null;
    let yesterday = null;

    for (let j = 0; j < deviceCluster.Devices.length; j++) {
      online = deviceCluster.Devices[j].Today;
      lastTwelve = deviceCluster.Devices[j].LastTwelve;
      yesterday = deviceCluster.Devices[j].Yesterday;
    }

    let state = null;
    if (!online && !lastTwelve && !yesterday) {
      // The device is considered completely offline and will not be drawn
      this.totalOffline++;
      return false;
    } else if (yesterday && !online && !lastTwelve) {
      state = false;
    } else if (!online && lastTwelve) {
      state = null;
    } else {
      state = true;
    }

    const iconInfo = this.tools.GetBasicTowerMarkerInfo(deviceCluster.Tower.MNC, null, state, true);
    const marker = new google.maps.Marker({
      label: {
        text: `${deviceCluster.Devices.length}`,
        color: 'black',
        fontSize: '10pt',
        fontWeight: 'bold',
        className: '',
      },
      MarkerIndex: deviceCluster.MarkerIndex,
      markerId: this.markers.length,
      position: new google.maps.LatLng(deviceCluster.Lat, deviceCluster.Long),
      map: this.map,
      CSQ: deviceCluster.CSQ,
      Tower: deviceCluster.Tower,
      Radius: deviceCluster.Radius,
      Devices: deviceCluster.Devices,
      icon: BASE_PATH + iconInfo.icon,
    });

    this.markers.push(marker);
    return true;
  }

  createDeviceListWindowContent(marker: google.maps.Marker): string {
    if (marker['Devices'] == null || marker['Devices'].length == 0) {
      // Invalid device list details
      return 'Data Unavailable.';
    }

    const network = this.tools.GetNetworkNameByMNC(marker['Tower'].MNC);
    let content = `
      <style>
        .gm-style-iw, .gm-style-iw-d{ height: 250px !important; max-height: 250px !important; }
      </style>
      <h3>Tower ${marker['Tower'].CellID}</h3>
      <div id="bodyContent">
        <p><span class="bold">Cell ID:</span> ${marker['Tower'].CellID ? marker['Tower'].CellID : ''}</p>
        <p><span class="bold">LAC:</span> ${marker['Tower'].LAC ? marker['Tower'].LAC : ''}</p>
        <p><span class="bold">Network:</span> ${network ? network : ''}</p>
      </div>
      <h4><u>Device(s):</u></h4>`;

    marker['Devices'].forEach((element: any, index: number) => {
      content +=
        `
      <div class="text-left">
        <p><span class="bold">${index + 1}:</span> ${element.Label} ` +
        (element.DeviceNum != null ? '(' + element.DeviceNum + ')' : '') +
        ` <a href="javascript:;" class="cursor pull-right" onClick="ViewDevice(${marker['Devices'][index].ID}, ${marker['Devices'][index]['DeviceID']})">View Details</a></p>
      </div>`;
    });
    content += `<br />`;

    return content;
  }

  navToDetails(kaid, cdrid) {
    if (this.activeInfoWindow == null || this.activeInfoWindow['Devices']?.length <= 0) {
      return;
    }

    for (let i = 0; i < this.activeInfoWindow['Devices'].length; i++) {
      if (this.activeInfoWindow['Devices'][i]['ID'] == kaid && this.activeInfoWindow['Devices'][i]['DeviceID'] == cdrid) {
        this.zone.run(() => {
          this.router.navigate([this.routeDictionary.KeepAliveDeviceDetails], {
            state: {
              data: {
                CdrID: cdrid,
                KaID: kaid,
                refresh: true,
              },
            },
          });
          return;
        });
      }
    }
  }

  ViewDevice(kaid, cdrid) {
    setTimeout(() => {
      window['MapComponent'].navToDetails(kaid, cdrid);
    });
  }

  CreateDeviceMapRadius(deviceCluster: any) {
    const csq = deviceCluster.CSQ != null ? deviceCluster.CSQ : null;
    const radiusDistance = deviceCluster.Radius != null && deviceCluster.Radius > 0 ? deviceCluster.Radius : null;
    const distance = this.tools.CalculateRadiusDistanceBasedOnCSQ(csq, radiusDistance);

    const red = this.tools.GetNetworkColourByMNC(deviceCluster.Tower.MNC);
    const radius = new google.maps.Circle({
      strokeColor: red,
      strokeOpacity: 0.4,
      strokeWeight: 2,
      fillColor: red,
      fillOpacity: 0.1,
      map: this.map,
      center: new google.maps.LatLng(deviceCluster.Lat, deviceCluster.Long),
      radius: distance,
    });
    this.circles.push(radius);
  }

  drawDeviceMapMarkers(data) {
    this.deviceMapData = [];
    const dupMap = {};

    if (!data) {
      data = [];
    }

    for (let i = 0; i < data.length; i++) {
      const temp = {
        MarkerIndex: 0,
        Lat: data[i].Lat,
        Long: data[i].Long,
        Tower: data[i].Tower,
        CSQ: data[i].CSQ,
        Radius: data[i].Radius,
        Devices: [
          {
            Label: data[i].Label,
            ID: data[i].ID,
            DeviceID: data[i].DeviceID,
            DeviceNum: data[i].DeviceNum,
            Timestamp: data[i].Timestamp,
            LastSeen: data[i].LastSeen,
            Today: data[i].Today,
            Yesterday: data[i].Yesterday,
            LastTwelve: data[i].LastTwelve,
          },
        ],
      };

      const center = new google.maps.LatLng(data[i].Lat, data[i].Long);
      const key = center.toString();

      if (this.deviceMapData.length == 0) {
        // No such location marker exists yet. Add new
        temp.MarkerIndex = 0;
        this.deviceMapData.push(temp);
        const success = this.CreateDeviceMapMarker(temp);
        if (success) {
          dupMap[key] = temp;
        }
      } else {
        if (dupMap[key] != null) {
          this.markers[dupMap[key].MarkerIndex]['Devices'].push(temp.Devices[0]);
          this.markers[dupMap[key].MarkerIndex].setLabel(this.markers[dupMap[key].MarkerIndex]['Devices'].length.toString());
        } else {
          // No such location marker exists yet. Add new
          temp.MarkerIndex = this.markers.length;
          this.deviceMapData.push(temp);
          const success = this.CreateDeviceMapMarker(temp);
          if (success) {
            dupMap[key] = temp;
          }
        }
      }
    }

    for (let i = 0; i < this.markers.length; i++) {
      // For each marker create the content window
      const infowindow = new google.maps.InfoWindow();
      google.maps.event.addListener(infowindow, 'closeclick', () => {
        this.closeInfoWindow();
      });

      google.maps.event.addListener(
        this.markers[i],
        'rightclick',
        ((marker: google.maps.Marker) => {
          return () => {
            if (this.activeInfoWindow != null) {
              this.activeInfoWindow.close();
            }

            for (let i = 0; i < this.circles.length; i++) {
              const radiusCenter = this.circles[i].getCenter();
              const markerCenter = marker.getPosition();
              if (radiusCenter.toString() == markerCenter.toString()) {
                this.circles[i].setMap(null);
                this.circles.splice(i, 1);
                return;
              }
            }
          };
        })(this.markers[i]),
      );

      google.maps.event.addListener(
        this.markers[i],
        'click',
        ((marker: google.maps.Marker) => {
          return () => {
            if (this.activeInfoWindow != null) {
              this.activeInfoWindow.close();
            }

            let sameMarkerClicked = false;
            for (let i = 0; i < this.activeInfoMarkers.length; i++) {
              if (marker == this.activeInfoMarkers[i]) {
                this.activeInfoWindow.close();

                for (let i = 0; i < this.circles.length; i++) {
                  const radiusCenter = this.circles[i].getCenter();
                  const markerCenter = marker.getPosition();
                  if (radiusCenter.toString() == markerCenter.toString()) {
                    this.circles[i].setMap(null);
                    this.circles.splice(i, 1);
                    break;
                  }
                }

                this.activeInfoMarkers.splice(i, 1);
                sameMarkerClicked = true;
                break;
              }
            }

            if (!sameMarkerClicked) {
              let content = ``;
              content = this.createDeviceListWindowContent(marker);

              infowindow.setOptions({
                content: content,
                maxWidth: 400,
                minWidth: 300,
                shouldFocus: true,
                disableAutoPan: true,
              });

              infowindow['Devices'] = marker['Devices'];

              const center = marker.getPosition().toJSON();
              const tmp = {
                MarkerIndex: marker['MarkerIndex'],
                Lat: center.lat,
                Long: center.lng,
                Tower: marker['Tower'],
                Radius: marker['Radius'],
                Devices: marker['Devices'],
                CSQ: marker['CSQ'],
              };

              if (!this.selectionEnabled) {
                this.clearCircles();
                this.CreateDeviceMapRadius(tmp);
                infowindow.open(this.map, marker);
                this.activeInfoWindow = infowindow;
              } else {
                this.CreateDeviceMapRadius(tmp);
              }
              this.activeInfoMarkers.push(marker);
            }
          };
        })(this.markers[i]),
      );
    }
  }

  GetDeviceMapData() {
    this.genericService
      .POST(
        this.endpoints.GET_DEVICE_MAP,
        {
          Search: this.currentSearch,
          IgnoreBounds: this.currentSearch != null && this.currentSearch.length > 0 ? true : false,
          Bounds: {
            SW: {
              Lat: this.mapBounds.south,
              Long: this.mapBounds.west,
            },
            NE: {
              Lat: this.mapBounds.north,
              Long: this.mapBounds.east,
            },
          },
        },
        true,
      )
      .subscribe(
        (results: any) => {
          if (results['body'] != null && results['body']['Locations'] != null && results['body']['Locations'].length > 0) {
            const data = results['body'].Locations;

            this.drawDeviceMapMarkers(data);
          }
          if (!this.clustering) {
            this.clearClusterer();
          } else {
            this.setDevicesClusterer();
          }

          if (this.showAllSignalStrength) {
            this.toggleAllSignalStrength(this.showAllSignalStrength);
          }
          if (this.showAllRingfence) {
            this.toggleAllRingfence(this.showAllRingfence);
          }
          this.maploading = false;
          this.changeDetectorRef.detectChanges();
        },
        () => {
          this.maploading = false;
          this.changeDetectorRef.detectChanges();
        },
      );
  }

  OptimiseClusterAmount(weight: number): number {
    let clusters: number;
    switch (true) {
      case weight >= 100:
        clusters = 15;
        break;
      case weight >= 70:
        clusters = 10;
        break;
      case weight >= 50:
        clusters = 7;
        break;
      case weight >= 30:
        clusters = 5;
        break;
      case weight >= 20:
        clusters = 3;
        break;
      default:
        clusters = 1;
        break;
    }
    return clusters;
  }

  GetClusterMapData(numClusters: number) {
    this.deviceMapData = [];
    let data = null;
    this.requestingClusterData = true;
    this.genericService
      .POST(
        this.endpoints.GET_DEVICE_CLUSTER_MAP,
        {
          Clusters: numClusters,
          IgnoreBounds: this.currentSearch != null && this.currentSearch.length > 0 ? true : false,
          Bounds: {
            SW: {
              Lat: this.mapBounds.south,
              Long: this.mapBounds.west,
            },
            NE: {
              Lat: this.mapBounds.north,
              Long: this.mapBounds.east,
            },
          },
        },
        true,
      )
      .subscribe(
        (results: any) => {
          if (results['body'].ClusterLocations) {
            data = results['body'].ClusterLocations;
            if (data != null && data.length > 0) {
              if (data.length == 1 && this.firstMapLoad) {
                this.numClusters = this.OptimiseClusterAmount(data[0].Weight);
                this.GetClusterMapData(this.numClusters);
              } else {
                data.forEach((cluster: any) => {
                  const marker = new google.maps.Marker({
                    position: { lat: cluster.Lat, lng: cluster.Long },
                    map: this.map,
                    icon: 'https://unpkg.com/@googlemaps/markerclustererplus@1.0.3/images/m3.png',
                    label: {
                      text: cluster.Weight.toString(),
                      color: 'white',
                      fontSize: '11pt',
                      fontWeight: 'bold',
                      className: null,
                    },
                  });
                  this.markers.push(marker);
                });
              }
            }
          } else {
            if (results['body']?.DeviceLocations?.length > 0) this.drawDeviceMapMarkers(results['body'].DeviceLocations);
          }

          if (!this.firstMapLoad) {
            this.maploading = false;
          }
          this.firstMapLoad = false;
          this.requestingClusterData = false;
          this.changeDetectorRef.detectChanges();
        },
        () => {
          this.maploading = false;
          this.firstMapLoad = false;
          this.changeDetectorRef.detectChanges();
        },
      );
  }

  closeInfoWindow() {
    if (this.activeInfoWindow) {
      this.activeInfoWindow.close();
    }

    this.activeInfoWindow = null;
  }

  setDevicesClusterer(): void {
    const styles = [
      MarkerClusterer.withDefaultStyle({
        width: 30,
        height: 30,
        url: '../assets/img/Group 9.png',
        textColor: '#ffffff',
        textSize: 10,
      }),
    ];

    this.clearClusterer();

    this.markerClusterer = new MarkerClusterer(this.map, this.markers, {
      gridSize: 30,
      maxZoom: 14,
      styles: styles,
      zoomOnClick: true,
      clusterClass: undefined,
      minimumClusterSize: 2,
      ignoreHiddenMarkers: true,
    });

    this.markerClusterer.setCalculator((markers) => {
      let total = 0;
      markers.forEach((element) => {
        total += element['Devices'] != null ? element['Devices'].length : 0;
      });

      return {
        text: total.toString(),
        index: 0,
      };
    });

    this.markerClusterer.repaint();
  }

  async showDeviceListModal(deviceIds: number[]) {
    const dialogRef = this.dialog.open(DeviceListModalComponent, {
      data: { deviceIds: deviceIds },
    });
  }

  searchSubmit(value: string): void {
    this.maploading = true;
    this.totalOffline = 0;
    this.clearMarkers();
    this.clearCircles();
    this.currentSearch = value;
    this.deviceMapData = [];
    if (value.length > 0) {
      this.freezeMap = true;
      this.GetDeviceMapData();
    } else {
      this.freezeMap = false;
      this.setMapRefresh(150);
    }
    this.changeDetectorRef.detectChanges();
  }

  searchValueChanged(): void {}

  filterToggled(): void {}

  toggleDeviceCluster() {
    this.clustering = !this.clustering;

    if (!this.clustering) {
      this.clearClusterer();
    } else {
      this.setDevicesClusterer();
    }
  }

  filtersCleared(): void {}

  toggleAllSignalStrength(toggle) {
    if (toggle) {
      for (let i = 0; i < this.markers.length; i++) {
        const exists = this.signalStrengthCircles.findIndex((t) => t['towerId'] == this.markers[i]['Tower'].CellID);
        if (exists == -1) {
          const distance = this.tools.CalculateRadiusDistanceBasedOnCSQ(this.markers[i]['CSQ'], this.markers[i]['Radius']);

          const red = this.tools.GetNetworkColourByMNC(this.markers[i]['Tower'].MNC);
          const radius = new google.maps.Circle({
            strokeColor: red,
            strokeOpacity: 0.4,
            strokeWeight: 2,
            fillColor: red,
            fillOpacity: 0.1,
            map: this.map,
            center: this.markers[i].getPosition(),
            radius: distance,
            towerId: this.markers[i]['Tower'].CellID,
          });

          this.signalStrengthCircles.push(radius);
        }
      }
    } else {
      if (this.signalStrengthCircles == null) {
        this.signalStrengthCircles = [];
        return;
      }
      for (let i = 0; i < this.signalStrengthCircles.length; i++) {
        this.signalStrengthCircles[i].setMap(null);
        this.signalStrengthCircles[i].setRadius(0);
      }
      this.signalStrengthCircles = [];
    }
  }

  toggleAllRingfence(toggle) {
    if (toggle) {
      for (let i = 0; i < this.markers.length; i++) {
        const exists = this.ringfenceCircles.findIndex((t) => t['towerId'] == this.markers[i]['Tower'].CellID);
        if (exists == -1) {
          const red = '#697479';
          const radius = new google.maps.Circle({
            strokeColor: red,
            strokeOpacity: 0.4,
            strokeWeight: 2,
            fillColor: red,
            fillOpacity: 0.1,
            map: this.map,
            center: this.markers[i].getPosition(),
            radius: this.markers[i]['Radius'],
            towerId: this.markers[i]['Tower'].CellID,
          });
          this.ringfenceCircles.push(radius);
        }
      }
    } else {
      if (this.ringfenceCircles == null) {
        this.ringfenceCircles = [];
        return;
      }
      for (let i = 0; i < this.ringfenceCircles.length; i++) {
        this.ringfenceCircles[i].setMap(null);
        this.ringfenceCircles[i].setRadius(0);
      }
      this.ringfenceCircles = [];
    }
  }
}
